import { useAuthContext } from '@app/core/auth/authentication/context';
import { useImpersonation } from '@app/core/auth/impersonation';
import { config } from '@app/config';
import { webApi } from '@mi-front/api-clients';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { buildVideoEditorProjectUrl } from './video-editor-project-url';

const autoProjectNameMessage = defineMessage({
  id: 'fragments.videoEditions.autoProjectName',
  defaultMessage: 'Proyecto {n}',
});

/**
 * Creates a video edition project for a fragment with a localized auto-generated name
 * ("Proyecto {n}" in es, etc.), then redirects to the video editor on success.
 */
export function useFragmentVideoEditionQuickCreate(fragmentId: string) {
  const { $t } = useIntl();
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { impersonatedUser } = useImpersonation();

  const { mutate, isPending } = webApi.useCreateProjectVideoEditorProjects({
    mutation: {
      onSuccess: async (project) => {
        await queryClient.invalidateQueries({
          queryKey: webApi.listProjectsVideoEditorProjectsQueryKey({ fragmentId }),
        });
        if (token) {
          const url = buildVideoEditorProjectUrl({
            baseUrl: config.videoEditorUrl,
            projectId: project.id,
            jwt: token,
            authAsUserId: impersonatedUser?.id,
          });
          window.location.assign(url);
        }
      },
    },
  });

  const createWithAutoName = useCallback(
    (currentProjectCount: number) => {
      const n = currentProjectCount + 1;
      const name = $t(autoProjectNameMessage, { n });
      mutate({
        data: {
          sourceType: webApi.CreateProjectDtoSourceTypeEnum.FRAGMENT,
          sourceId: fragmentId,
          name,
        },
      });
    },
    [$t, mutate, fragmentId],
  );

  return {
    createWithAutoName,
    isCreating: isPending,
  };
}
